import type { Bean } from "./model";

/**
 * All tunable numbers for the predictor and the target recipe. Mirrors PredictorConfig.java field for field.
 */
export interface PredictorConfig {
  /** recipe dose in grams (logged, not used by the model in v1) */
  readonly targetDoseG: number;
  /** recipe yield in grams; shot times are normalised to this yield */
  readonly targetYieldG: number;
  /** desired shot time in seconds */
  readonly targetTimeS: number;
  /** below this normalised time a shot is channeled / too coarse */
  readonly minValidTimeS: number;
  /** above this normalised time a shot is choked / too fine */
  readonly maxValidTimeS: number;
  /** k: how many shots the grinder's prior slope is worth when blending */
  readonly priorPseudoShots: number;
  /** shot weight halves every this many days */
  readonly recencyHalfLifeD: number;
  /** beans run this fraction faster per day of effective age (ln-space) */
  readonly agingRatePerDay: number;
  /** degassing drift stops after this many days of effective age */
  readonly agingCapDays: number;
  /** minimum weighted SD of grind setting before a slope is fitted */
  readonly minGrindSpread: number;
  /** censored shots older than this are not used as bounds */
  readonly boundLookbackDays: number;
  /** ln-space noise assumed when fewer than three valid shots exist */
  readonly defaultResidualSd: number;
  /** weight of the starting-point pseudo-observation in the Step 7 centroid */
  readonly priorInterceptWeight: number;
  /** decaf beans age this much faster (multiplies agingRatePerDay) */
  readonly decafAgingMultiplier: number;
  /** past beans' contribution to x_ref halves every this many days since their last shot */
  readonly referenceHalfLifeD: number;
  /** minimum n_eff (as of the bean's last shot) for a bean to count as dialed in */
  readonly xDialedMinNeff: number;
  /** minimum valid shots before a bean's attributes are learned from */
  readonly learningMinValidShots: number;
  /** k in the offset update: (delta·(n+k) + observed)/(n+k+1) */
  readonly learningPseudoCount: number;
  /** |δ_bean| is clamped to this (ln-time) */
  readonly deltaBeanClamp: number;
  /** half-width of the recipe time window (target ± this); the chart band and taste feedback read it */
  readonly targetWindowHalfS: number;
  /** grinder slope learning: the seed slope is worth this many beans */
  readonly slopePseudoBeans: number;
  /** grinder slope learning: a bean needs this many valid shots … */
  readonly slopeMinValidShots: number;
  /** … spanning at least this many dial steps */
  readonly slopeMinSpanSteps: number;
  /** grinder slope learning: a bean's weight halves every this many days since its last shot */
  readonly slopeHalfLifeD: number;
}

/** Bump whenever any default below changes; stored on each shot for future backtesting. Mirrors Java. */
export const CONFIG_VERSION = "2026-10-06.1"; // neutral roast/density seeds

export function defaultConfig(): PredictorConfig {
  return {
    targetDoseG: 18.0,
    targetYieldG: 40.0,
    targetTimeS: 27.5,
    minValidTimeS: 15.0,
    maxValidTimeS: 50.0,
    priorPseudoShots: 4.0,
    recencyHalfLifeD: 7.0,
    agingRatePerDay: 0.015,
    agingCapDays: 21,
    minGrindSpread: 0.1,
    boundLookbackDays: 14,
    defaultResidualSd: 0.1,
    priorInterceptWeight: 1.0,
    decafAgingMultiplier: 1.5,
    referenceHalfLifeD: 60.0,
    xDialedMinNeff: 3.0,
    learningMinValidShots: 4,
    learningPseudoCount: 3.0,
    deltaBeanClamp: 0.5,
    targetWindowHalfS: 2.5,
    slopePseudoBeans: 5.0,
    slopeMinValidShots: 4,
    slopeMinSpanSteps: 3.0,
    slopeHalfLifeD: 365.0,
  };
}

/** Same validation as the Java record's compact constructor. */
export function validateConfig(cfg: PredictorConfig): PredictorConfig {
  if (cfg.targetYieldG <= 0 || cfg.targetTimeS <= 0) throw new RangeError("targets must be positive");
  if (cfg.minValidTimeS >= cfg.maxValidTimeS) throw new RangeError("minValidTimeS must be < maxValidTimeS");
  if (cfg.priorPseudoShots < 0) throw new RangeError("priorPseudoShots must be >= 0");
  if (cfg.recencyHalfLifeD <= 0) throw new RangeError("recencyHalfLifeD must be positive");
  if (cfg.priorInterceptWeight < 0) throw new RangeError("priorInterceptWeight must be >= 0");
  if (cfg.decafAgingMultiplier <= 0) throw new RangeError("decafAgingMultiplier must be positive");
  if (cfg.referenceHalfLifeD <= 0) throw new RangeError("referenceHalfLifeD must be positive");
  if (cfg.targetWindowHalfS <= 0) throw new RangeError("targetWindowHalfS must be positive");
  if (cfg.slopePseudoBeans < 0) throw new RangeError("slopePseudoBeans must be >= 0");
  if (cfg.slopeHalfLifeD <= 0) throw new RangeError("slopeHalfLifeD must be positive");
  return cfg;
}

/** The config to use for a bean: its own target time when set, otherwise the config unchanged (same object). */
export function effectiveConfig(cfg: PredictorConfig, bean: Bean | null): PredictorConfig {
  if (bean === null || bean.targetTimeS === null || bean.targetTimeS === cfg.targetTimeS) return cfg;
  return { ...cfg, targetTimeS: bean.targetTimeS };
}

/** The user's recipe: dose, yield and the target time window. The target time is the middle of the window. */
export interface Recipe {
  readonly doseG: number;
  readonly yieldG: number;
  readonly timeLowS: number;
  readonly timeHighS: number;
}

export function defaultRecipe(): Recipe {
  return { doseG: 18.0, yieldG: 40.0, timeLowS: 25.0, timeHighS: 30.0 };
}

/** Throws RangeError with a user-facing message. Mirrors PredictorConfig.Recipe. */
export function validateRecipe(r: Recipe): Recipe {
  if (!(r.doseG > 0)) throw new RangeError("Dose must be a positive number");
  if (!(r.yieldG > 0)) throw new RangeError("Yield must be a positive number");
  if (!(r.timeLowS > 0) || !(r.timeHighS > r.timeLowS)) throw new RangeError("Time window must run from a lower to a higher time");
  return r;
}

/** The config with the recipe applied: dose, yield, target = window middle, window half-width. */
export function withRecipe(cfg: PredictorConfig, r: Recipe): PredictorConfig {
  return validateConfig({ ...cfg, targetDoseG: r.doseG, targetYieldG: r.yieldG,
    targetTimeS: (r.timeLowS + r.timeHighS) / 2, targetWindowHalfS: (r.timeHighS - r.timeLowS) / 2 });
}

/** The four parameters automatic tuning may change. */
export interface TunedParams {
  readonly agingRatePerDay: number;
  readonly recencyHalfLifeD: number;
  readonly priorPseudoShots: number;
  readonly priorInterceptWeight: number;
}

export function tunedParamsOf(cfg: PredictorConfig): TunedParams {
  return { agingRatePerDay: cfg.agingRatePerDay, recencyHalfLifeD: cfg.recencyHalfLifeD,
           priorPseudoShots: cfg.priorPseudoShots, priorInterceptWeight: cfg.priorInterceptWeight };
}

export function withTuned(cfg: PredictorConfig, p: TunedParams): PredictorConfig {
  return validateConfig({ ...cfg, ...p });
}
